import { NumberInputBlock } from '@coast/schemas/index';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useContext, useState } from 'react';
import { MBButton } from '@shared/components/MBButton';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { MBTextInput } from '@shared/components/textInput';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { NumberFormat } from '@shared/data/entity/fields/NumberFormat';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { getTextInputSize, isInputBold } from '../input';
import { AnchoredSheetInputBlock } from '../inputBottomSheet';

interface Props {
  block: NumberInputBlock;
}

export function MBCardInputBlockNumber({ block }: Props) {
  const colors = useContext(ColorThemeContext);
  const { setFieldValue, handleBlur, values } = useCardFormContext();

  const storedValue = Number(values[block.name]);
  const format = NumberFormat.convertNumberModeToNumberFormat(block.fieldType);
  const editableValue = NumberField.toEditableValue(storedValue, format);

  const [input, setInput] = useState(editableValue);
  const [focused, setFocused] = useState(block.autoFocus);

  const onFocus = useCallback(() => setFocused(true), []);
  const onBlur = useCallback(() => {
    setFocused(false);
    handleBlur(block.name);
    setInput(prev => {
      const numericValue = NumberField.toStoredValue(prev, format);
      return NumberField.toEditableValue(numericValue, format);
    });
  }, [handleBlur, block.name, format]);

  const onChange = useCallback(
    (textValue: string) => {
      if (textValue === '.') {
        setInput('0.');
        return;
      }

      if (isEmpty(textValue)) {
        setInput(textValue);
        setFieldValue(block.name, undefined);
        return;
      }

      // remove all non-numeric input, allowing number-related characters
      const cleanedInput = NumberField.removeInvalidInput(textValue);
      setInput(cleanedInput);

      const numericValue = NumberField.toStoredValue(textValue, format);
      if (isNil(numericValue)) {
        return;
      }

      setFieldValue(block.name, numericValue);
    },
    [setFieldValue, block, format],
  );

  const onReset = useCallback(() => {
    setInput(NumberField.toEditableValue(storedValue, format));
  }, [storedValue, format]);

  const storableValue = NumberField.toStoredValue(input, format);
  const formattedValue = NumberField.toFormattedValue(storableValue, format);

  return (
    <AnchoredSheetInputBlock<string>
      stateless
      block={block}
      onReset={onReset}
      renderPlaceholder={() => (focused ? input : formattedValue)}
      renderInput={({ mode }) => (
        <MBTextInput
          noBorder
          noPadding
          keyboardType="decimal-pad"
          autoFocus={mode === 'CREATE' ? block.autoFocus : false}
          value={focused ? input : formattedValue}
          onChangeText={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          size={block.textStyle ? getTextInputSize(block.textStyle) : undefined}
          bold={block.textStyle ? isInputBold(block.textStyle) : undefined}
          placeholder={block.placeholder}
        />
      )}
      renderEditForm={({ onSave }) => {
        return (
          <View fill padding={2}>
            <MBTextInput
              noPadding
              keyboardType="decimal-pad"
              style={{
                backgroundColor: colors.backgroundInactive,
                borderRadius: grid(1),
                padding: grid(2),
              }}
              onChangeText={setInput}
              value={input}
              placeholder={block.placeholder}
              autoFocus
            />
            <View paddingBottom={2} />
            <MBButton
              title={i18n('common.ok')}
              onPress={() => {
                onChange(input);
                onSave();
              }}
            />
          </View>
        );
      }}
    />
  );
}
